import { useEffect, useState } from "react";
import type { AnswerMark, AnswerMarkStatus } from "@quizshow/protocol";

const statusLabels: Record<AnswerMarkStatus, string> = {
  correct: "Richtig",
  incorrect: "Falsch",
  mentioned: "Genannt",
  revealed: "Aufgedeckt",
};

export interface ManualScoreTarget {
  type: "player" | "team";
  id: string;
  label: string;
}

export function AnswerMarkControl({
  mark,
  scoreTargets,
  onChange,
  onManualSolve,
}: {
  mark?: AnswerMark;
  scoreTargets: ManualScoreTarget[];
  onChange: (value: {
    status?: AnswerMarkStatus;
    releasedToParticipants?: boolean;
    releasedToStream?: boolean;
  }) => void;
  onManualSolve: (
    target?: ManualScoreTarget,
    visibility?: {
      markReleasedToParticipants?: boolean;
      markReleasedToStream?: boolean;
      releasedToParticipants?: boolean;
      releasedToStream?: boolean;
    },
  ) => void;
}) {
  const [targetValue, setTargetValue] = useState(
    scoreTargets[0] ? `${scoreTargets[0].type}:${scoreTargets[0].id}` : "",
  );
  useEffect(() => {
    if (!scoreTargets.length) {
      setTargetValue("");
      return;
    }
    if (!scoreTargets.some((target) => `${target.type}:${target.id}` === targetValue)) {
      setTargetValue(`${scoreTargets[0]!.type}:${scoreTargets[0]!.id}`);
    }
  }, [scoreTargets, targetValue]);
  const selectedTarget = scoreTargets.find((target) => `${target.type}:${target.id}` === targetValue);
  const setStatus = (status: AnswerMarkStatus) =>
    onChange({
      status,
      releasedToParticipants: mark?.releasedToParticipants ?? false,
      releasedToStream: mark?.releasedToStream ?? false,
    });
  return (
    <div className="answer-mark-control">
      {mark ? (
        <div className="answer-status-badges">
          <span className={`answer-status-badge answer-status-badge--${mark.status}`}>{statusLabels[mark.status]}</span>
          {mark.releasedToParticipants ? <span className="answer-status-badge">Teilnehmer</span> : null}
          {mark.releasedToStream ? <span className="answer-status-badge">Stream</span> : null}
        </div>
      ) : null}
      <button
        className="answer-inline-solve"
        type="button"
        disabled={!selectedTarget}
        title={
          selectedTarget
            ? `Punkte oder Rangfortschritt für ${selectedTarget.label} anwenden`
            : "Zuerst ein Team oder einen Spieler als Wertungsziel anlegen"
        }
        onClick={() => onManualSolve(selectedTarget, { markReleasedToParticipants: true })}
      >
        Richtig werten
      </button>
      <details className="answer-mark-menu">
        <summary className="answer-menu-trigger" aria-label="Antwortaktionen">
          …
        </summary>
        <div className="answer-mark-menu__panel">
          <span className="eyebrow">Markierung</span>
          <button type="button" onClick={() => setStatus("mentioned")}>Als genannt markieren</button>
          <button type="button" onClick={() => setStatus("correct")}>Nur als richtig markieren</button>
          <button type="button" onClick={() => setStatus("incorrect")}>Als falsch markieren</button>
          <button type="button" onClick={() => setStatus("revealed")}>Aufdecken</button>
          <span className="eyebrow">Sichtbarkeit</span>
          <button
            type="button"
            disabled={!mark}
            onClick={() =>
              onChange({
                status: mark?.status,
                releasedToParticipants: mark?.releasedToParticipants ?? false,
                releasedToStream: true,
              })
            }
          >
            Für Stream anzeigen
          </button>
          <button
            type="button"
            disabled={!mark}
            onClick={() =>
              onChange({
                status: mark?.status,
                releasedToParticipants: true,
                releasedToStream: mark?.releasedToStream ?? false,
              })
            }
          >
            Für Teilnehmer anzeigen
          </button>
          <button
            type="button"
            disabled={!mark}
            onClick={() =>
              onChange({
                status: mark?.status,
                releasedToParticipants: false,
                releasedToStream: false,
              })
            }
          >
            Nur intern anzeigen
          </button>
          <button type="button" disabled={!mark} onClick={() => onChange({})}>Markierung löschen</button>
          <span className="eyebrow">Manuelle Wertung</span>
          <select
            aria-label="Wertungsziel"
            value={targetValue}
            onChange={(event) => setTargetValue(event.target.value)}
          >
            {!scoreTargets.length ? <option value="">Kein Wertungsziel vorhanden</option> : null}
            {scoreTargets.map((target) => (
              <option key={`${target.type}:${target.id}`} value={`${target.type}:${target.id}`}>
                Wertung an {target.label}
              </option>
            ))}
          </select>
          <button
            className="answer-menu-solve"
            type="button"
            disabled={!selectedTarget}
            onClick={() => onManualSolve(selectedTarget, { markReleasedToParticipants: true })}
          >
            Richtig werten
          </button>
          <button
            type="button"
            disabled={!selectedTarget}
            onClick={() =>
              onManualSolve(selectedTarget, {
                markReleasedToStream: true,
                releasedToStream: true,
              })
            }
          >
            Richtig werten + Lösung im Stream anzeigen
          </button>
          <button
            type="button"
            disabled={!selectedTarget}
            onClick={() =>
              onManualSolve(selectedTarget, {
                markReleasedToParticipants: true,
                releasedToParticipants: true,
              })
            }
          >
            Richtig werten + Lösung für Teilnehmer anzeigen
          </button>
          <button
            type="button"
            disabled={!selectedTarget}
            onClick={() =>
              onManualSolve(selectedTarget, {
                markReleasedToParticipants: true,
                markReleasedToStream: true,
                releasedToParticipants: true,
                releasedToStream: true,
              })
            }
          >
            Richtig werten + Lösung für beide anzeigen
          </button>
        </div>
      </details>
    </div>
  );
}
